<template>
  <article class="container-fluid">
    <PageHeader :page="page" />

    <FilterManager
      v-model="this.filters"
      :filters="filtersHeader"
      :display-advanced="false"
      @academicYear="
        updateFiltersData('academicYear', $event, 'onAcademicYearChanged')
      "
      @region="updateFiltersData('region', $event, 'onRegionChanged')"
      @municipality="
        updateFiltersData('municipality', $event, 'onMunicipalityChanged')
      "
      @search="onSearch"
    />

    <CustomSeparator
      text="Resultado de Asistentes"
      class="my-4 custom-separator-line"
    />

    <template v-if="showNotResults">
      <CustomResultNotFound
        class="mt-4 row pt-3"
        text="No hay información disponible."
      />
    </template>

    <template v-else>
      <div class="search-result">
        <SearchResultRow
          v-for="(item, index) in collections.searchResult"
          :key="`row-item-${index}`"
          ref="resultRow"
          :value="item"
        />

        <Pagination
          v-if="pagination.totalItemCount > pagination.perPage"
          :data="pagination"
          @pagination-go-page="onPageChanged"
        />
      </div>
    </template>
  </article>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import CustomSeparator from 'custom-separator';
import academicYearService from '@/services/academicyear';
import locationService from '@/services/api/location.api.js';
import {
  getRequestStatusList,
  getServiceAssistantSchools,
} from '@/modules/human-resources/api';
import SearchResultRow from './components/SearchResultRow.vue';
import storeMixin from '@/mixin/storeGenerator';
import schoolTypes from '@/utils/constants/student/schoolTypes';

export default {
  name: 'ServiceAssitants',
  mixins: [storeMixin],
  components: {
    PageHeader,
    FilterManager,
    CustomResultNotFound,
    CustomSeparator,
    Pagination,
    SearchResultRow,
  },
  data() {
    return {
      page: {
        icon: 'fas fa-user-edit',
        title: 'Manejo de Asistente de Servicio',
        routes: [
          'Inicio',
          'Recursos Humanos',
          'Manejo de Asistente de Servicio',
        ],
      },
      collections: {
        academicYears: [],
        regions: [],
        municipalities: [],
        schools: [],
        searchResult: [],
      },
      filters: {
        academicYear: null,
        region: null,
        municipality: null,
        school: null,
      },
      pagination: {
        perPage: 20,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
    };
  },
  methods: {
    goAddServiceAssistant() {
      this.$router.push('/humanres/serviceAssistantManagement/add');
    },
    async onSearch(filters, page = 1) {
      const mappedFilter = {
        academicYearId: (filters.academicYear || {}).id,
        regionId: (filters.region || {}).id,
        municipalityId: (filters.municipality || {}).id,
        schoolId: (filters.school || {}).id,
        pageIndex: page,
      };

      const {
        data,
        count,
        currentPage,
        pageCount,
        totalItemCount,
      } = await getServiceAssistantSchools(mappedFilter);

      this.collections.searchResult = [...data];

      this.pagination.totalItemCount = totalItemCount;
      this.pagination.count = count;
      this.pagination.currentPage = currentPage;
      this.pagination.pageCount = pageCount;
    },
    async onPageChanged(currentPage) {
      this.pagination.currentPage = currentPage;
      await this.onSearch(this.filters, currentPage);
    },
    async fetchResources() {
      await academicYearService
        .getAcademicYearFromMin({
          yearType: 'FUTURE',
        })
        .then((res) => {
          const nonValidAcademicYears = [28,29,30] //1995, 1996, 1997
          this.collections.academicYears = res.data.filter(x => !nonValidAcademicYears.includes(x.id));
          this.filters.academicYear = res.data.find((e) => e.selected);
        });

      this.fetchRegions(this.filters.academicYear?.id);
    },
    updateFiltersData(key, event, callback) {
      if (event != undefined && event != null && event != '') {
        this.filters[key] = event;
      } else {
        this.filters[key] = null;
      }

      if (callback != undefined) {
        if (typeof this[callback] == 'function') {
          this[callback](event);
        }
      }
    },
    async onAcademicYearChanged(academicYear) {
      this.filters.region = null;
      this.filters.municipality = null;
      this.filters.school = null;

      this.collections.regions = [];
      this.collections.municipalities = [];
      this.collections.schools = [];

      await this.fetchRegions(academicYear.id);
    },
    async onRegionChanged(region) {
      this.filters.municipality = null;
      this.filters.school = null;

      this.collections.municipalities = [];
      this.collections.schools = [];

      if (region) {
        await this.fetchMunicipalities(region.id);
      }
    },
    async onMunicipalityChanged(municipality) {
      this.filters.school = null;

      this.collections.schools = [];

      if (municipality) {
        await this.fetchSchools(municipality.id);
      }
    },
    async fetchRegions(academicYearId) {
      const { data } = await locationService.getRegions({ academicYearId });
      this.collections.regions = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.region = data[0];
        this.regionDisabled = true;

        await this.fetchMunicipalities(data[0]);
      } else {
        this.regionDisabled = false;
      }
    },
    async fetchMunicipalities(regionId) {
      const { data } = await locationService.getMunicipalities({
        regionId,
        academicYearId: this.filters.academicYear.id,
      });

      this.collections.municipalities = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.municipality = data[0];
        this.municipalityDisabled = true;

        await this.fetchSchools(data[0]);
      } else {
        this.municipalityDisabled = false;
      }
    },
    async fetchSchools(municipalityId) {
      const { data } = await locationService.getSchools({
        schoolTypes: [schoolTypes.PublicSchool, schoolTypes.Institutions],
        municipalityId,
        academicYearId: this.filters.academicYear.id,
      });
      this.collections.schools = data;

      if (data != null && data.length == 1 && data[0].selected) {
        this.filters.school = data[0];
        this.schoolDisabled = true;
      } else {
        this.schoolDisabled = false;
      }
    },
    async fetchQuickFiltersData() {
      const academicYears = await academicYearService.getAcademicYearFromMin();

      this.collections.academicYears = academicYears;

      if (academicYears != null && academicYears.some((e) => e.selected)) {
        this.filters.academicYear = academicYears.find((e) => e.selected);
      } else if (academicYears != null && academicYears.length == 1) {
        this.filters.academicYear = academicYears[0];
      }
      const { data: statusList } = await getRequestStatusList(this.filters.academicYear.id);

      this.collections.status = statusList;
    },
  },
  computed: {
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.collections.academicYears,
          value: this.filters.academicYear,
          placeholder: 'Seleccione el año',
          fieldName: 'description',
          fieldKey: 'id',
          clearable: false,
          class: 'col-12 col-md ml-0 mr-0',
          classContainer: 'w-100',
          validations: 'required',
          mapParentValue: this.filters.academicYear == null,
        },
        {
          name: 'region',
          component: 'CustomDropDown',
          options: this.collections.regions,
          value: this.filters.region,
          placeholder: 'Región',
          class: 'col-12 col-md d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.region == null,
          disabled: this.regionDisabled,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.collections.municipalities,
          value: this.filters.municipality,
          placeholder: 'Municipio',
          class: 'col-12 col-md d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.municipality == null,
          disabled: this.municipalityDisabled,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          options: this.collections.schools,
          value: this.filters.school,
          placeholder: 'Escuela',
          class: 'col-12 col-md-5 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.school == null,
          disabled: this.schoolDisabled,
        },
      ];
    },
    showNotResults() {
      return this.collections.searchResult.length == 0;
    },
  },
  async mounted() {
    await this.fetchResources();
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const persistRoutes = ['ManageServiceAssistant'];

    next(async (vm) => {
      if (persistRoutes.some((x) => x == from.name)) {
        vm.recoveryData();
      } else {
        vm.shouldRecovery = false;
        vm.showAdvanced = false;

        await vm.fetchResources();
      }
    });
  },
};
</script>

<style></style>
